import { useState, useEffect } from 'react'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faYoutube } from '@fortawesome/free-brands-svg-icons'
import { faSpinner, faExclamationTriangle, faPlayCircle } from '@/fontawesome'
import { fetchChannelVideos, type YouTubeVideo } from '@/services/youtube.api'

const CHANNEL_ID = 'UC0dRdPIX_FP3yJmiTKcm3qw'

export default function YouTubeVideosSection() {
  const [videos, setVideos] = useState<YouTubeVideo[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    const loadVideos = async () => {
      try {
        setLoading(true)
        setError(null)
        const fetchedVideos = await fetchChannelVideos(CHANNEL_ID, 6)
        setVideos(fetchedVideos)
      } catch (err) {
        console.error('Failed to load YouTube videos:', err)
        setError(err instanceof Error ? err.message : 'Fehler beim Laden der Videos')
      } finally {
        setLoading(false)
      }
    }

    loadVideos()
  }, [])

  const formatDate = (dateString: string): string => {
    const date = new Date(dateString)
    return date.toLocaleDateString('de-DE', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    })
  }

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg p-6 sm:p-8 shadow-md border border-gray-200 dark:border-gray-700">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 bg-red-100 dark:bg-red-900/50 rounded-lg flex items-center justify-center">
            <FontAwesomeIcon icon={faYoutube} className="w-6 h-6 text-red-600 dark:text-red-400" />
          </div>
          <div>
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
              Neueste Videos
            </h2>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              Trading-Analysen und Marktkommentare
            </p>
          </div>
        </div>

        <a
          href={`https://www.youtube.com/channel/${CHANNEL_ID}`}
          target="_blank"
          rel="noopener noreferrer"
          className="text-red-600 dark:text-red-400 hover:text-red-700 dark:hover:text-red-300 text-sm font-medium transition-colors cursor-pointer"
        >
          Zum Kanal →
        </a>
      </div>

      {loading && (
        <div className="flex items-center justify-center py-12">
          <div className="text-center">
            <FontAwesomeIcon icon={faSpinner} className="w-8 h-8 text-red-600 dark:text-red-400 animate-spin mb-3" />
            <p className="text-sm text-gray-500 dark:text-gray-400">Lade Videos...</p>
          </div>
        </div>
      )}

      {error !== null && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-6">
          <div className="flex items-center gap-3 mb-3">
            <FontAwesomeIcon icon={faExclamationTriangle} className="w-6 h-6 text-red-600 dark:text-red-400" />
            <h3 className="font-semibold text-red-900 dark:text-red-300">
              Videos konnten nicht geladen werden
            </h3>
          </div>
          <p className="text-sm text-red-700 dark:text-red-400 mb-3">
            {error}
          </p>
          <p className="text-xs text-red-600 dark:text-red-500">
            Bitte stellen Sie sicher, dass ein YouTube API Key in den Environment Variables konfiguriert ist (VITE_YOUTUBE_API_KEY).
          </p>
        </div>
      )}

      {!loading && error === null && videos.length === 0 && (
        <div className="text-center py-12">
          <p className="text-gray-500 dark:text-gray-400">
            Keine Videos gefunden
          </p>
        </div>
      )}

      {!loading && error === null && videos.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {videos.map((video, index) => (
            <a
              key={video.id}
              href={video.videoUrl}
              target="_blank"
              rel="noopener noreferrer"
              className={`group block bg-gray-50 dark:bg-gray-700/50 rounded-lg overflow-hidden hover:shadow-lg transition-shadow cursor-pointer ${index >= 3 ? 'hidden md:block' : ''}`}
            >
              {/* Thumbnail */}
              <div className="relative aspect-video bg-gray-200 dark:bg-gray-600">
                <img
                  src={video.thumbnailUrl}
                  alt={video.title}
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                  <FontAwesomeIcon icon={faPlayCircle} className="w-16 h-16 text-white" />
                </div>
              </div>

              {/* Content */}
              <div className="p-4">
                <h3 className="font-semibold text-gray-900 dark:text-white mb-2 line-clamp-2 group-hover:text-red-600 dark:group-hover:text-red-400 transition-colors">
                  {video.title}
                </h3>
                <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">
                  {formatDate(video.publishedAt)}
                </p>
                <p className="text-sm text-gray-600 dark:text-gray-300 line-clamp-2">
                  {video.description}
                </p>
              </div>
            </a>
          ))}
        </div>
      )}
    </div>
  )
}
